import { assign, raise } from "xstate"
import { ActionObject, ExtractEvent } from "xstate/lib/types"
import * as F from "fp-ts/function"
import * as ROA from "fp-ts/ReadonlyArray"
import * as O from "fp-ts/Option"
import * as ORD from "fp-ts/Ord"
import * as N from "fp-ts/number"
import { CheckoutProcessId } from "@ssc-booking/api-contracts"
import { match, P } from "ts-pattern"
import { FareCard, FareCardStatus } from "../../read-models/fare-card"
import { ECommerceEvent } from "./e-commerce-machine"
import { CheckoutState, ECommerceContext, StartedCheckoutState } from "./e-commerce-machine-context"

type Context = ECommerceContext
type Events = ECommerceEvent

export const notifyInvalidParameter = (parameterName: string, value: unknown) =>
    raise<Context, Events, Events>({
        type: "NOTIFY",
        kind: "invalidParameter",
        origin: "ecommerce",
        info: { parameterName, value },
    })

export const sendAllFaresEvents = (
    oldFareCards: readonly FareCard[],
    newFareCard: FareCard,
): readonly ActionObject<Context, Events>[] => {
    const newFareCards = updateFareCard(newFareCard)(oldFareCards)
    return [
        ...maybeSendAllFaresSelectable(oldFareCards, newFareCards),
        ...maybeSendAllFaresInError(oldFareCards, newFareCards),
    ]
}

const maybeSendAllFaresSelectable = (oldFareCards: readonly FareCard[], newFareCards: readonly FareCard[]) =>
    F.pipe(
        O.guard(!areAllSelectable(oldFareCards) && areAllSelectable(newFareCards)),
        O.fold(
            () => [],
            () => [raise<Context, Events>({ type: "ALL_FARES_SELECTABLE" })],
        ),
    )
const areAllSelectable = (fareCards: readonly FareCard[]) => overallStatus(fareCards) != "unknown"

const maybeSendAllFaresInError = (oldFareCards: readonly FareCard[], newFareCards: readonly FareCard[]) =>
    F.pipe(
        overallStatus(newFareCards),
        O.fromPredicate((newStatus) => overallStatus(oldFareCards) != newStatus),
        O.chain(O.fromPredicate(isError)),
        O.fold(
            () => [],
            (error) => [raise<Context, Events>({ type: "ALL_FARES_IN_ERROR", error })],
        ),
    )
const isError = (x: FareCardStatus): x is Exclude<FareCardStatus, "unknown" | "good"> => x !== "unknown" && x !== "good"

const overallStatus = (fareCards: readonly FareCard[]): FareCardStatus =>
    F.pipe(fareCards, ROA.map(status), ROA.sort(byPriority), ROA.head, O.getOrElseW(unknown))
const status = (x: FareCard) => x.status
const unknown = () => "unknown" as const
const byPriority = F.pipe(
    N.Ord,
    ORD.contramap((x: FareCardStatus) => statusPriority[x]),
)
const statusPriority: Record<FareCardStatus, number> = {
    unknown: 1,
    good: 2,
    noAvailablePrice: 3,
    transient: 4,
    missingCruise: 5,
    fatal: 6,
}

export const assignFareCard = (newFareCard: FareCard, ctx: Context) =>
    assign<Context, Events>({ fareCards: updateFareCard(newFareCard)(ctx.fareCards) })
export const updateFareCard = (newFareCard: FareCard) => (fareCards: readonly FareCard[]) =>
    F.pipe(
        fareCards,
        ROA.map((x) => (x.key === newFareCard.key ? newFareCard : x)),
    )

export const raiseFareCardChanged = (fareCard: FareCard) =>
    raise<Context, Events, Events>({ type: "FARE_CARD_CHANGED", fareCard })

const optionCreatedByStatus: Record<CheckoutState["status"], boolean> = {
    tobeValidated: false,
    valid: false,
    invalid: false,
    started: true,
    completed: true,
    failed: false,
}
export const isCheckoutStarted = (ctx: Context) => optionCreatedByStatus[ctx.checkoutStatus.status]

export const isAtLeastCheckoutStarted = (checkout: CheckoutState): checkout is StartedCheckoutState =>
    optionCreatedByStatus[checkout.status]

export const checkoutId = (ctx: Pick<Context, "checkoutStatus" | "sessionId">): CheckoutProcessId | undefined =>
    match(ctx.checkoutStatus)
        .with({ status: P.union("tobeValidated", "invalid", "failed") }, F.constUndefined)
        .with({ status: "valid" }, () => CheckoutProcessId.fromUUID(ctx.sessionId))
        .with({ status: "started" }, () => CheckoutProcessId.fromUUID(ctx.sessionId))
        .with({ status: "completed" }, () => CheckoutProcessId.fromUUID(ctx.sessionId))
        .exhaustive()

export const bookingNumber = (ctx: Context): string | undefined =>
    match(ctx.checkoutStatus)
        .with({ status: P.union("tobeValidated", "invalid", "valid", "failed") }, F.constUndefined)
        .with({ status: "started" }, (x) => x.response.bookingNumber)
        .with({ status: "completed" }, (x) => x.response.bookingNumber)
        .exhaustive()

export const validationError = (ctx: Context) =>
    match(ctx.checkoutStatus)
        .with({ status: P.union("tobeValidated", "valid", "started", "completed", "failed") }, F.constUndefined)
        .with({ status: "invalid" }, (x) => x.response.errorCode)
        .exhaustive()

export const sendCheckoutValidationError = (ev: ExtractEvent<Events, "CHECKOUT_NOT_VALIDATED">) =>
    match(ev.response)
        .with({ errorCode: P.union("invalidEmail", "invalidCaptcha", "mismatchedVsNumber") }, F.constUndefined)
        .otherwise((response) =>
            raise<Context, Events>({
                type: "ERROR",
                origin: "ecommerce",
                kind: "generic",
                errorCode: response.errorCode,
                info: { response },
                message: "Load error validate-checkout",
            }),
        )

export const sendCheckoutFailedError = (ev: ExtractEvent<Events, "CHECKOUT_FAILED">) =>
    match(ev.response)
        .with({ errorCode: "suiteNoMoreAvailable" }, (response) =>
            raise<Context, Events>({
                type: "NOTIFY",
                origin: "ecommerce",
                kind: "selectedSuiteNoMoreAvailable",
                info: { response },
            }),
        )
        .with({ errorCode: "cancelled" }, (response) =>
            raise<Context, Events>({
                type: "NOTIFY",
                origin: "ecommerce",
                kind: "checkoutProcessExpired",
                info: { response },
            }),
        )
        .otherwise((response) =>
            raise<Context, Events>({
                type: "ERROR",
                origin: "ecommerce",
                kind: "fatal",
                errorCode: response.errorCode,
                info: { response },
                message: "checkout process failed",
            }),
        )
